import { useRef, useState } from "react";
import {
	ResponsiveContainer,
	BarChart,
	XAxis,
	YAxis,
	Bar,
	LabelList,
	ReferenceLine,
} from "recharts";
import { numberFormatter } from "../../../utils";
import "../baseline.scss";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink } from "react-csv";

const DistrictSpendByL4 = (props: any) => {
	const [tooltip, setTooltip] = useState<any>({
		district: "",
		show: false,
		costPercent: 0,
		l4Category: "",
		l3:"",
	});

	const exportFullRef = useRef<any>(null);
	const csvHeaders = [
		{ label: "L1", key: "l1" },
		{ label: "L2", key: "l2" },
		{ label: "L3", key: "l3" },
		{ label: "District", key: "District" },
		{ label: "L4", key: "category" },
		{ label: "Spend", key: "costPercent" },
	];

	const exportData = () => {
		if (props.exportDistrictData?.length !== 0) {
			exportFullRef?.current?.link.click();
		}
	};

	const renderCustomizedLabel = (props: any) => {
		const { x, y, width, values, height } = props;
		return (
			<>
				{values?.category?.length > 0 && width > 180 && (
					<text
						x={x + width / 2}
						y={
							y -
							(values.category.length > 0
								? -(height - 10)
								: -(height - 10))
						}
						fill="#fff"
						textAnchor="middle"
						dominantBaseline="middle"
						className={
							(values.category.length > 15 && width < 180) ||
							(values.category.length > 30 && width < 400)
								? "text-[1.2vh] text-wrap"
								: "text-[1.5vh] text-wrap"
						}
					>
						{values.category}
					</text>
				)}
			</>
		);
	};

	const onHover = (data: any, index: number) => {
		setTooltip({
			show: true,
			district: data.payload.District,
			costPercent: data.value,
			l4Category: data.dataKey,
			l3: props.l3,
		});
	};

	const onOut = () => {
		setTooltip({ show: false, district: "", costPercent: "", l3:"" });
	};

	return (
		<table className="common-table-ui">
			<thead>
				<tr>
					<th className="text-left">District</th>
					<th>% of Total Spend</th>
					<th colSpan={1}>
						<div className="flex w-full">
							<span className="w-[90%]">
								District spend by L4
							</span>
							<div>
								<FileDownloadIcon
									className="ml-[2vh] cursor-pointer"
									style={{
										background: "#9acd32b8",
										borderRadius: ".3vh",
										fill: "white",
									}}
									onClick={() => {
										exportData();
									}}
								/>
								<CSVLink
									ref={exportFullRef}
									data={props.exportDistrictData}
									headers={csvHeaders}
									filename="districtSummary.csv"
									className="hidden"
									target="_blank"
								></CSVLink>
							</div>
						</div>
					</th>
				</tr>
			</thead>
			<tbody>
				{props?.data?.map((d: any, index: any) => {
					return (
						<tr key={index} className="h-[8vh]">
							<td className="w-[10%]">{d.District}</td>
							<td className="w-[10%] text-center">
								{d.totalSpendPrcntDistrict.toFixed(2)}%
							</td>
							<td className="w-[50%] relative h-full flex w-full">
								<ResponsiveContainer
									width={`${
										(d.netcost / props.data[0].netcost) *
										100
									}%`}
									height="100%"
									minHeight={"6vh"}
								>
									<BarChart
										data={[d]}
										layout="vertical"
										stackOffset="expand"
									>
										<XAxis hide type="number" />
										<YAxis
											hide
											type="category"
											dataKey="District"
										/>
										{d.l4Spend.map(
											(l4Category: any, ind: number) => {
												return (
													<Bar
														key={
															l4Category.category
														}
														dataKey={
															l4Category.category
														}
														stackId="a"
														onMouseEnter={(
															a: any,
															index
														) =>
															onHover(
																a
																	.tooltipPayload[0],
																index
															)
														}
														onMouseLeave={onOut}
														fill={l4Category.Color}
													>
														<LabelList
															dataKey="total"
															values={l4Category}
															content={
																renderCustomizedLabel
															}
														/>
													</Bar>
												);
											}
										)}
										{/* {< ReferenceLine x={0.8} isFront={true} strokeWidth={3} label={index % 5 == 0 && { position: 'top', fill: '#a4f504', value: "80% Spend", fontStyle: 'italic' }}></ReferenceLine>} */}
									</BarChart>
								</ResponsiveContainer>
								<span>{numberFormatter(d.netcost, 2)}</span>
								{tooltip.show &&
									tooltip.district === d.District && (
										<div
											style={{
												position: "absolute",
												bottom: "7.5vh",
												left: 0,
												zIndex: 999,
												width: "fit-content",
												background: "white",
												border: "1px solid #ccc",
												boxShadow:
													"0 2px 5px rgba(0,0,0,0.2)",
												borderRadius: "1vh",
												cursor: "pointer",
												color: "black",
												opacity: 0.97,
											}}
										>
											{/* Header Row */}
											<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
												<div className="font-bold uppercase">
													L4:
												</div>
												<div
													className="font-bold ml-1"
													style={{ color: "#3f3f3f" }}
												>
													{tooltip.l4Category}
												</div>
											</div>
											{/* Data Row */}
											<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
												<p
													className="label"
													style={{
														padding: "0",
														margin: "0",
													}}
												>
													<label
														className="graph-label-small"
														style={{
															color: "#5a5a5a",
															fontWeight:
																"normal",
														}}
													>
														Spend:{" "}
													</label>
													<label
														className="graph-label-small"
														style={{
															color: "#3f3f3f",
															fontWeight: "bold",
														}}
													>
														{numberFormatter(
															tooltip.costPercent,
															2, "$"
														)}
													</label>
												</p>
											</div>
										</div>
									)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};

export default DistrictSpendByL4;
